import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { journalService, progressService, dailyContentService } from '../services/api';
import Navigation from '../components/Navigation';
import LoadingSpinner from '../components/LoadingSpinner';
import SOSButton from '../components/SOSButton';
import { Button, Card } from '../components/ui';

const PHASE_NAMES = ['Stabilize', 'Understand', 'Rebuild', 'Sustain'];

const MOOD_OPTIONS = [
  { label: 'Struggling', value: 1, color: '#E24B4A' },
  { label: 'Difficult', value: 3, color: '#EF9F27' },
  { label: 'Neutral', value: 5, color: '#888780' },
  { label: 'Good', value: 7, color: '#1D9E75' },
  { label: 'Thriving', value: 9, color: '#0F6E56' },
];

const QUICK_PROMPTS = [
  'What is one thing I am grateful for today, even if it feels small?',
  'What emotion felt most present today? What do I think brought it on?',
  'If I could change one thing about how I handled today, what would it be?',
];

const moodScoreToLabel = (score) => {
  if (score == null) return null;
  if (score <= 2) return MOOD_OPTIONS[0];
  if (score <= 4) return MOOD_OPTIONS[1];
  if (score <= 6) return MOOD_OPTIONS[2];
  if (score <= 8) return MOOD_OPTIONS[3];
  return MOOD_OPTIONS[4];
};

const wordCount = (text) => {
  if (!text || !text.trim()) return 0;
  return text.trim().split(/\s+/).length;
};

const JournalView = () => {
  const { user } = useAuth();
  const addTextareaRef = useRef(null);
  const [entries, setEntries] = useState([]);
  const [progress, setProgress] = useState(null);
  const [dailyPrompt, setDailyPrompt] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveSuccess, setSaveSuccess] = useState(false);

  // Whether today's entry already exists in the database
  const [entrySaved, setEntrySaved] = useState(false);
  const [savedText, setSavedText] = useState('');

  // Form state — used for first save or for the "add more" textarea
  const [selectedMood, setSelectedMood] = useState(null);
  const [stressLevel, setStressLevel] = useState(5);
  const [energyLevel, setEnergyLevel] = useState(5);
  const [challengeIntensity, setChallengeIntensity] = useState(3);
  const [journalText, setJournalText] = useState('');
  const [additionText, setAdditionText] = useState('');
  const [showPrompts, setShowPrompts] = useState(false);

  const currentDay = user?.currentDay || 1;
  const currentPhase = user?.currentPhase || 1;
  const phaseName = PHASE_NAMES[currentPhase - 1] || 'Stabilize';
  const streak = progress?.currentStreak || 0;

  const loadData = useCallback(async () => {
    try {
      const results = await Promise.allSettled([
        journalService.getAllEntries(user._id),
        progressService.getProgress(user.id || user._id),
        dailyContentService.getDay(currentDay),
      ]);

      if (results[0].status === 'fulfilled') {
        setEntries(results[0].value.data.data.entries || []);
      }
      if (results[1].status === 'fulfilled') {
        setProgress(results[1].value.data.data.progress);
      }
      if (results[2].status === 'fulfilled') {
        const content = results[2].value.data.data.content;
        if (content?.journal?.prompts?.length > 0) {
          setDailyPrompt(content.journal.prompts[0]);
        } else if (content?.journal?.title) {
          setDailyPrompt(content.journal.title);
        }
      }

      // Load today's existing entry if any
      try {
        const entryRes = await journalService.getEntry(user._id, currentDay);
        const entry = entryRes.data.data.entry;
        if (entry) {
          setEntrySaved(true);
          const moodVal = entry.tracking?.moodScale;
          const matched = MOOD_OPTIONS.find(m => m.value === moodVal);
          if (matched) setSelectedMood(matched);
          setStressLevel(entry.tracking?.stressLevel || 5);
          setEnergyLevel(entry.tracking?.energyLevel || 5);
          setChallengeIntensity(entry.tracking?.challengeIntensity ?? entry.tracking?.cravingScale ?? 3);
          let existingText = '';
          if (entry.content?.text) {
            existingText = entry.content.text;
          } else if (typeof entry.content === 'string') {
            existingText = entry.content;
          }
          setSavedText(existingText);
          setJournalText(existingText);
        }
      } catch {
        // No entry for today yet
      }
    } catch (error) {
      console.error('Failed to load journal data:', error);
    } finally {
      setLoading(false);
    }
  }, [user._id, user.id, currentDay]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleSave = async () => {
    if (!selectedMood) {
      alert('Please select how you are feeling today.');
      return;
    }

    setSaving(true);
    setSaveSuccess(false);

    try {
      let finalText;
      if (entrySaved) {
        // Append-only: merge saved text with addition
        finalText = additionText.trim()
          ? savedText + '\n\n---\n\n' + additionText.trim()
          : savedText;
      } else {
        finalText = journalText;
      }

      const entryData = {
        day: currentDay,
        content: { text: finalText },
        tracking: {
          moodScale: selectedMood.value,
          cravingScale: challengeIntensity,
          challengeIntensity,
          stressLevel,
          energyLevel,
        },
        completed: { journal: true },
      };

      await journalService.createEntry(user._id, entryData);
      setSaveSuccess(true);
      setEntrySaved(true);
      setSavedText(finalText);
      setJournalText(finalText);
      setAdditionText('');

      // Refresh entries
      const res = await journalService.getAllEntries(user._id);
      setEntries(res.data.data.entries || []);

      // Refresh progress for streak
      try {
        const progressRes = await progressService.getProgress(user.id || user._id);
        setProgress(progressRes.data.data.progress);
      } catch { /* non-critical */ }
    } catch (error) {
      console.error('Failed to save journal entry:', error);
      alert('Failed to save journal entry. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handlePromptClick = (prompt) => {
    if (entrySaved) {
      setAdditionText(prev => {
        const separator = prev.trim() ? '\n\n' : '';
        return prev + separator + prompt;
      });
    } else {
      setJournalText(prev => {
        const separator = prev.trim() ? '\n\n' : '';
        return prev + separator + prompt;
      });
    }
    if (addTextareaRef.current) {
      addTextareaRef.current.focus();
    }
  };

  // Get today's entry completion status
  const todayEntry = entries.find(e => e.day === currentDay);
  const pillars = {
    mind: todayEntry?.completed?.mind || false,
    move: todayEntry?.completed?.move || false,
    journal: todayEntry?.completed?.journal || false,
  };

  const recentEntries = entries.filter(e => e.day !== currentDay).slice().reverse().slice(0, 5);

  if (loading) {
    return <LoadingSpinner message="Loading journal..." />;
  }

  const activeWordCount = entrySaved
    ? wordCount(additionText)
    : wordCount(journalText);

  return (
    <>
      <Navigation />
      <main className="mx-auto max-w-3xl px-6 py-10">

        {/* Page Header */}
        <div className="mb-8 flex items-start justify-between">
          <div>
            <h1 className="text-3xl font-display font-semibold text-neutral-900">Journal</h1>
            <p className="mt-1 text-neutral-500">
              Day {currentDay} &middot; {phaseName} Phase &middot;{' '}
              {new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
          <div className="text-right flex-shrink-0">
            <div className="inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1.5 text-sm font-medium text-primary-800">
              <span className="text-lg">🔥</span> {streak} day streak
            </div>
            <div className="mt-2 flex gap-1.5 justify-end">
              {['Mind', 'Move', 'Journal'].map((p) => {
                const key = p.toLowerCase();
                const active = pillars[key];
                return (
                  <span
                    key={key}
                    className={`inline-block rounded-full px-2.5 py-0.5 text-xs font-medium ${
                      active
                        ? 'bg-green-100 text-green-700'
                        : key === 'journal'
                          ? 'bg-primary-100 text-primary-700 ring-1 ring-primary-300'
                          : 'bg-neutral-100 text-neutral-500'
                    }`}
                  >
                    {active ? '✓ ' : ''}{p}
                  </span>
                );
              })}
            </div>
          </div>
        </div>

        {/* Guided Reflection Prompt */}
        {dailyPrompt && (
          <div className="mb-8 rounded-lg bg-white p-6 shadow-sm" style={{ borderLeft: '3px solid #1D9E75' }}>
            <div className="flex items-start justify-between mb-3">
              <p className="text-xs font-semibold uppercase tracking-wider text-neutral-400">
                Today's reflection prompt
              </p>
              <span className="text-xs font-medium text-neutral-500 bg-neutral-100 px-2 py-0.5 rounded">
                CBT &middot; Day {currentDay}
              </span>
            </div>
            <p className="text-lg leading-relaxed text-neutral-800" style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}>
              {typeof dailyPrompt === 'string' ? dailyPrompt : dailyPrompt.question || dailyPrompt}
            </p>
          </div>
        )}

        {/* Emotional Check-in */}
        <Card className="mb-6 p-6">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400 mb-4">
            How are you feeling today?
          </h3>
          <div className="flex gap-2">
            {MOOD_OPTIONS.map((mood) => {
              const isSelected = selectedMood?.value === mood.value;
              return (
                <button
                  key={mood.value}
                  onClick={() => !entrySaved && setSelectedMood(mood)}
                  disabled={entrySaved}
                  className={`flex-1 rounded-lg py-3 px-2 text-sm font-medium transition-all border-2 ${entrySaved ? 'cursor-default opacity-80' : ''}`}
                  style={{
                    borderColor: isSelected ? mood.color : '#e5e5e5',
                    backgroundColor: isSelected ? `${mood.color}21` : 'transparent',
                    color: isSelected ? mood.color : '#6b7280',
                    fontWeight: isSelected ? 500 : 400,
                  }}
                >
                  {mood.label}
                </button>
              );
            })}
          </div>
          {selectedMood && (
            <p className="mt-2 text-xs text-neutral-500">
              {entrySaved ? `Recorded: ${selectedMood.label}` : `Selected: ${selectedMood.label}`}
            </p>
          )}
        </Card>

        {/* Wellness Check Sliders */}
        <Card className="mb-6 p-6">
          <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400 mb-4">
            Wellness check
          </h3>
          <div className="space-y-4">
            {[
              { label: 'Stress level', value: stressLevel, setter: setStressLevel },
              { label: 'Energy level', value: energyLevel, setter: setEnergyLevel },
              { label: 'Challenge intensity', value: challengeIntensity, setter: setChallengeIntensity },
            ].map(({ label, value, setter }) => (
              <div key={label} className="flex items-center gap-4">
                <span className="text-sm text-neutral-700" style={{ minWidth: 140 }}>{label}</span>
                <input
                  type="range"
                  min={1}
                  max={10}
                  step={1}
                  value={value}
                  disabled={entrySaved}
                  onChange={(e) => setter(parseInt(e.target.value, 10))}
                  className={`flex-1 h-2 rounded-lg appearance-none cursor-pointer accent-primary-600 ${entrySaved ? 'opacity-60' : ''}`}
                  style={{ background: `linear-gradient(to right, #1D9E75 0%, #1D9E75 ${((value - 1) / 9) * 100}%, #e5e7eb ${((value - 1) / 9) * 100}%, #e5e7eb 100%)` }}
                />
                <span className="text-sm font-medium text-neutral-800" style={{ minWidth: 24, textAlign: 'right' }}>
                  {value}
                </span>
              </div>
            ))}
          </div>
          <p className="mt-3 text-xs text-neutral-400">
            {entrySaved
              ? 'Wellness scores are recorded with your first save and cannot be changed.'
              : 'Challenge intensity reflects any difficult urge, thought, or feeling — not only cravings.'}
          </p>
        </Card>

        {/* Saved Entry (read-only) — shown after first save */}
        {entrySaved && savedText && (
          <Card className="mb-6 p-6">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
                Today's saved entry
              </h3>
              <div className="flex items-center gap-1.5 text-xs text-green-600">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M20 6 9 17l-5-5"/>
                </svg>
                Saved
              </div>
            </div>
            <div
              className="text-neutral-700 whitespace-pre-wrap"
              style={{
                fontFamily: "Georgia, 'Times New Roman', serif",
                fontSize: 15,
                lineHeight: 1.7,
              }}
            >
              {savedText}
            </div>
          </Card>
        )}

        {/* Journal Entry Text Area — new entry or add-more */}
        <Card className="mb-6 p-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400">
              {entrySaved ? 'Add to today\'s entry' : 'Journal entry'}
            </h3>
            <div className="flex items-center gap-1.5 text-xs text-neutral-400">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
              Private &amp; encrypted
            </div>
          </div>

          {entrySaved && (
            <div className="mb-4 rounded-md bg-blue-50 border border-blue-200 px-4 py-3 text-sm text-blue-800">
              Your entry for today has been saved. You can add more thoughts below — additions will be appended to your existing entry. Previous text cannot be edited.
            </div>
          )}

          <textarea
            ref={addTextareaRef}
            value={entrySaved ? additionText : journalText}
            onChange={(e) => entrySaved ? setAdditionText(e.target.value) : setJournalText(e.target.value)}
            placeholder={entrySaved
              ? 'Add more thoughts, reflections, or notes to today\'s entry...'
              : 'Begin writing here. This space is yours — private, encrypted, and never read by any staff or clinician…'
            }
            rows={entrySaved ? 5 : 8}
            className="w-full resize-y border-0 bg-transparent outline-none text-neutral-800 placeholder-neutral-400"
            style={{
              fontFamily: "Georgia, 'Times New Roman', serif",
              fontSize: 15,
              lineHeight: 1.7,
              minHeight: entrySaved ? 100 : 160,
            }}
          />
          <div className="flex items-center justify-between pt-3 border-t border-neutral-100 mt-2">
            <span className="text-xs text-neutral-400">
              {activeWordCount} {activeWordCount === 1 ? 'word' : 'words'}
            </span>
            <span className="inline-flex items-center gap-1 rounded-full bg-blue-50 px-2.5 py-0.5 text-xs font-medium text-blue-700">
              {user?.accountType === 'education' || user?.accountType === 'institutional' ? 'FERPA' : 'HIPAA'} disclosure active
            </span>
          </div>
        </Card>

        {/* Quick-start Prompts */}
        <div className="mb-6">
          <button
            onClick={() => setShowPrompts(!showPrompts)}
            className="flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-700 transition-colors"
          >
            <span className="text-base">{showPrompts ? '−' : '+'}</span>
            Not sure where to start? Try a prompt
          </button>
          {showPrompts && (
            <div className="mt-3 grid gap-2">
              {QUICK_PROMPTS.map((prompt, idx) => (
                <button
                  key={idx}
                  onClick={() => handlePromptClick(prompt)}
                  className="text-left rounded-lg border border-neutral-200 bg-white p-4 text-sm text-neutral-700 hover:border-primary-300 hover:bg-primary-50 transition-colors"
                  style={{ fontFamily: "Georgia, 'Times New Roman', serif" }}
                >
                  "{prompt}"
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Recent Entries (past days only) */}
        {recentEntries.length > 0 && (
          <Card className="mb-6 p-6">
            <h3 className="text-sm font-semibold uppercase tracking-wider text-neutral-400 mb-4">
              Past entries
            </h3>
            <div className="space-y-2">
              {recentEntries.map((entry) => {
                const moodInfo = moodScoreToLabel(entry.tracking?.moodScale);
                return (
                  <div
                    key={entry._id}
                    className="flex items-center gap-4 rounded-lg border border-neutral-200 px-4 py-3 hover:border-neutral-300 transition-colors cursor-pointer"
                    onClick={() => window.location.href = `/daily/${entry.day}`}
                  >
                    <span className="text-sm text-neutral-500 w-24">
                      {new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                    </span>
                    <span className="text-sm text-neutral-700 w-16">Day {entry.day}</span>
                    <span className="text-xs text-neutral-400 flex-1">View entry</span>
                    {moodInfo && (
                      <span
                        className="inline-block rounded-full px-2.5 py-0.5 text-xs font-medium"
                        style={{
                          borderWidth: 1,
                          borderStyle: 'solid',
                          borderColor: moodInfo.color,
                          backgroundColor: `${moodInfo.color}1A`,
                          color: moodInfo.color,
                        }}
                      >
                        {moodInfo.label}
                      </span>
                    )}
                  </div>
                );
              })}
            </div>
          </Card>
        )}

        {/* Save Action Bar */}
        <div className="mb-8">
          <Button
            onClick={handleSave}
            disabled={saving || (entrySaved && !additionText.trim())}
            className="w-full justify-center"
            variant={saveSuccess ? 'success' : 'primary'}
          >
            {saving
              ? 'Saving...'
              : saveSuccess
                ? 'Entry saved'
                : entrySaved
                  ? 'Save addition'
                  : "Save today's entry"}
          </Button>
          {!entrySaved && (
            <p className="mt-3 text-center text-xs text-neutral-500">
              Once saved, your entry is permanent. You can add more thoughts for today, but previous text cannot be edited.
            </p>
          )}
          <p className="mt-2 text-center text-xs text-neutral-400">
            Complete all 3 pillars to maintain your {streak}-day streak
          </p>
        </div>
      </main>

      <SOSButton variant="floating" />
    </>
  );
};

export default JournalView;
